import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, H2, Typography } from '@saviynt/design-system';

import { CF_NEU_BLD, CF_SEC_MED } from '../../constants';

import './CustomDonutChart.css';

/**
 * Easing function for smooth animation (easeOutQuad)
 */
const easeOutQuad = (t: number): number => t * (2 - t);

/**
 * Easing function for bounce effect (easeOutBounce)
 */
const easeOutBounce = (t: number): number => {
  if (t < 1 / 2.75) {
    return 7.5625 * t * t;
  }

  if (t < 2 / 2.75) {
    const t2 = t - 1.5 / 2.75;

    return 7.5625 * t2 * t2 + 0.75;
  }

  if (t < 2.5 / 2.75) {
    const t2 = t - 2.25 / 2.75;

    return 7.5625 * t2 * t2 + 0.9375;
  }

  const t2 = t - 2.625 / 2.75;

  return 7.5625 * t2 * t2 + 0.984375;
};

/**
 * Generates SVG path segments for donut chart visualization with Highcharts-style hover effects
 * @param data - Array of data items with category and value
 * @param colorRange - Array of colors for each segment
 * @param width - Chart width
 * @param height - Chart height
 * @param innerRadius - Inner radius for donut hole
 * @param animationProgress - Progress of animation from 0 to 1
 * @param onSegmentHover - Callback for segment hover events
 * @param onSegmentLeave - Callback for segment leave events
 * @param onSegmentClick - Callback for segment click events
 * @param hoveredCategory - Currently hovered segment category
 * @param hoverConfig - Configuration for hover effects (halo, brightness, dimming)
 * @returns Array of React group elements containing segment paths and hover effects
 */
function generateDonutSegments(
  data: { category: string; value: number }[],
  colorRange: string[],
  width: number,
  height: number,
  innerRadius: number,
  animationProgress = 1,
  onSegmentHover?: (
    item: { category: string; value: number },
    event: React.MouseEvent
  ) => void,
  onSegmentLeave?: () => void,
  onSegmentClick?: (
    item: { category: string; value: number },
    event: React.MouseEvent
  ) => void,
  hoveredCategory?: string | null,
  hoverConfig?: {
    enableHalo?: boolean;
    haloWidth?: number;
    haloOpacity?: number;
  }
): React.ReactElement[] {
  // Calculate chart center and outer radius with padding
  const centerX = width / 2;
  const centerY = height / 2;
  const outerRadius = Math.min(width, height) / 2 - 2;

  // Calculate total value for proportional segment sizing
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // If all values are 0, create a single gray segment to show the chart
  if (total === 0) {
    const grayPath = [
      `M ${centerX} ${centerY - outerRadius}`,
      `A ${outerRadius} ${outerRadius} 0 1 1 ${centerX} ${
        centerY + outerRadius
      }`,
      `A ${outerRadius} ${outerRadius} 0 1 1 ${centerX} ${
        centerY - outerRadius
      }`,
      `M ${centerX} ${centerY - innerRadius}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${centerX} ${
        centerY + innerRadius
      }`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${centerX} ${
        centerY - innerRadius
      }`,
      'Z',
    ].join(' ');

    return [<path key='empty' d={grayPath} fill='#E5E7EB' />];
  }

  // Create small gaps between segments for visual separation
  const gapAngle = (1 / outerRadius) * data.length;
  const totalAngle = 2 * Math.PI - gapAngle;

  // Start from top of circle (-90 degrees)
  let cumulativeAngle = -Math.PI / 2;

  return data.map((item, index) => {
    // Calculate proportional angle for this segment
    const fullAngle = (item.value / total) * totalAngle;
    const angle = fullAngle * animationProgress;

    // Add gap between segments (except for first segment)
    const startAngle =
      cumulativeAngle + (index > 0 ? gapAngle / data.length : 0);
    const endAngle = startAngle + angle;

    cumulativeAngle = endAngle;

    // Determine if arc spans more than 180 degrees for SVG arc flag
    const largeArcFlag = angle > Math.PI ? 1 : 0;

    // Calculate outer arc coordinates
    const x1 = centerX + outerRadius * Math.cos(startAngle);
    const y1 = centerY + outerRadius * Math.sin(startAngle);
    const x2 = centerX + outerRadius * Math.cos(endAngle);
    const y2 = centerY + outerRadius * Math.sin(endAngle);

    // Calculate inner arc coordinates
    const x3 = centerX + innerRadius * Math.cos(endAngle);
    const y3 = centerY + innerRadius * Math.sin(endAngle);
    const x4 = centerX + innerRadius * Math.cos(startAngle);
    const y4 = centerY + innerRadius * Math.sin(startAngle);

    // Create SVG path: outer arc → line → inner arc → close
    const pathData = [
      `M ${x1} ${y1}`, // Move to start of outer arc
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`, // Outer arc
      `L ${x3} ${y3}`, // Line to inner arc
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`, // Inner arc (reverse)
      'Z', // Close path
    ].join(' ');

    // Determine hover states for current segment
    const isHovered = hoveredCategory === item.category;
    const isOtherHovered = hoveredCategory && hoveredCategory !== item.category;

    // Extract hover configuration with defaults
    const showHalo = hoverConfig?.enableHalo ?? true;
    const haloWidth = hoverConfig?.haloWidth ?? 10;
    const haloOpacity = hoverConfig?.haloOpacity ?? 0.3;

    // Create outer arc path for halo effect (only outer circumference)
    const haloPath = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

    return (
      <g key={item.category}>
        {/* Main segment path with hover effects */}
        <path
          d={pathData}
          fill={colorRange[index]}
          className={`Sod-Donut-chart-segment ${isHovered ? 'hovered' : ''} ${
            isOtherHovered ? 'dimmed' : ''
          }`}
          onMouseEnter={(e) => onSegmentHover?.(item, e)}
          onMouseLeave={onSegmentLeave}
          onClick={(e) => onSegmentClick?.(item, e)}
          style={{
            transition: 'filter 0.15s ease-out, opacity 0.25s ease-out',
            cursor: 'pointer',
          }}
        />
        {/* Halo effect - outer arc glow on hover */}
        {isHovered && showHalo && (
          <path
            d={haloPath}
            fill='none'
            stroke={colorRange[index]}
            strokeWidth={haloWidth}
            opacity={haloOpacity}
            style={{
              transition: 'opacity 0.15s ease-out',
              pointerEvents: 'none',
            }}
          />
        )}
      </g>
    );
  });
}

interface DonutChartProps {
  data: { category: string; value: number }[];
  colorRange: string[];
  value: number;
  label: string;
  width: number;
  height: number;
  innerRadius: number;
  darkMode?: boolean;
  animate?: boolean;
  animationDuration?: number;
  easing?: 'easeOutQuad' | 'easeOutBounce';
  onSegmentClick?: (
    item: { category: string; value: number },
    event: React.MouseEvent
  ) => void;
  hoverConfig?: {
    enableHalo?: boolean;
    haloWidth?: number;
    haloOpacity?: number;
  };
}

/**
 * SodCustomDonutChart Component
 *
 * SVG-based donut chart visualization component that renders segmented circular
 * charts with customizable colors and displays centered value/label text for
 * data representation in AAG EntitlementDetails UsersTab.
 *
 * @component
 * @returns {React.ReactElement} Donut chart with centered text displaying:
 *   - Segmented circular chart based on data proportions
 *   - Color-coded segments using provided colorRange
 *   - Centered value and label text overlay
 *   - Configurable dimensions and inner radius
 *   - Small gaps between segments for visual separation
 *  @example
 *  <SodCustomDonutChart
 *    data={[{ category: 'Open', value: 40 },]}
 *    colorRange={colorRange}
 *    value={total}
 *    label={'Total Items'}
 *    width={140}
 *    height={140}
 *    innerRadius={60}
 *    animate={false}
 *    animationDuration={1000}
 *    easing='easeOutQuad'
 *    hoverConfig={{
 *      enableHalo: true,
 *      haloWidth: 10,
 *      haloOpacity: 0.3
 *    }}
 *  />
 */
function SodCustomDonutChart({
  data,
  colorRange,
  value,
  label,
  width,
  height,
  innerRadius,
  darkMode = false,
  animate,
  animationDuration,
  easing,
  onSegmentClick,
  hoverConfig,
}: DonutChartProps): React.ReactElement {
  const [animationProgress, setAnimationProgress] = useState(animate ? 0 : 1);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    category: string;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const hasAnimated = useRef(false);

  const handleSegmentHover = useCallback(
    (item: { category: string; value: number }, event: React.MouseEvent) => {
      setHoveredCategory(item.category);
      setTooltip({
        visible: true,
        category: item.category,
        value: item.value,
        x: event.clientX,
        y: event.clientY,
      });
    },
    []
  );

  const handleSegmentLeave = useCallback(() => {
    setHoveredCategory(null);
    setTooltip(null);
  }, []);

  useEffect(() => {
    if (!animate || hasAnimated.current) {
      setAnimationProgress(1);

      return undefined;
    }

    hasAnimated.current = true;
    setAnimationProgress(0);

    const startTime = Date.now();
    const easingFunction =
      easing === 'easeOutBounce' ? easeOutBounce : easeOutQuad;
    let animationFrameId: number;

    const animateChart = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (animationDuration ?? 1000), 1);
      const easedProgress = easingFunction(progress);

      setAnimationProgress(easedProgress);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateChart);
      }
    };

    animationFrameId = requestAnimationFrame(animateChart);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [animate, animationDuration, easing]);

  const segments = generateDonutSegments(
    data,
    colorRange,
    width,
    height,
    innerRadius,
    animationProgress,
    handleSegmentHover,
    handleSegmentLeave,
    onSegmentClick,
    hoveredCategory,
    hoverConfig
  );

  return (
    <Box
      className={`Sod-Donut-chart-container ${darkMode ? 'dark-mode' : ''}`}
      style={{ width, height }}>
      {tooltip &&
        ReactDOM.createPortal(
          <div
            className='Sod-Donut-chart-tooltip'
            style={{
              left: tooltip.x,
              top: tooltip.y,
            }}>
            <div className='Sod-Donut-chart-tooltip-category'>
              {tooltip.category}
            </div>
            <div className='Sod-Donut-chart-tooltip-value'>{tooltip.value}</div>
          </div>,
          document.body
        )}
      <svg
        width={width}
        height={height}
        className='Sod-Donut-chart-svg'
        role='img'
        aria-label={`Donut chart showing ${label}: ${value} total across ${data.length} categories`}>
        <desc>
          {data.map((item) => `${item.category}: ${item.value}`).join(', ')}
        </desc>
        {segments}
      </svg>
      <Box className='Sod-Donut-chart-center'>
        <H2 className='Sod-Donut-chart-center-value' color={CF_NEU_BLD}>
          {value}
        </H2>
        <Typography
          className='Sod-Donut-chart-center-label'
          kind='body2'
          color={CF_SEC_MED}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

SodCustomDonutChart.defaultProps = {
  darkMode: false,
  animate: true,
  animationDuration: 1000,
  easing: 'easeOutQuad' as const,
  onSegmentClick: undefined,
  hoverConfig: {
    enableHalo: true,
    haloWidth: 10,
    haloOpacity: 0.3,
  },
};

export default SodCustomDonutChart;
